/* eslint-disable camelcase */
/* eslint-disable sonarjs/no-duplicate-string */
import {
  ActionCard,
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Pill,
  Spinner,
  Stack,
  toast,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import {
  INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS,
  INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE,
  IndicatorProductSubscriptionConfiguration,
} from '@/api/rest/resources/types/IndicatorProduct';
import { SuspenseBoundary } from '@/components';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { Card } from '@/components/widgets/o/Card/Card';
import {
  useGetIndicatorProductSubscriptionConfigurations,
  useRemoveIndicatorProductSubscriptionConfiguration,
  useUpdateIndicatorProductSubscriptionConfiguration,
} from '@/hooks/useDashboardIndicatorProductSubscriptionConfiguration';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import { Logger } from '@/lib/logs/logger';
import { useCanManageInsituData } from '@/pages/shared/uploads/hooks/useCanManageInsituData';
import { isNullish } from '@/utils/booleans';
import { printDDMMYYYY } from '@/utils/formatting/date';
import { getStyleForIPConfigStatus } from '@/utils/getStyleForIPConfigStatus';

import { AddNewConfig } from './AddNewConfig';
import { InSituFileUploadDialog } from './InSituFileUploadDialog';

export const BUTTON_STYLES = 'aspect-square py-1 px-2 rounded-full';

export const InSituDataCards = () => {
  const { t } = useTranslation();

  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <Card
          styles={{
            root: 'rounded-3xl p-4',
          }}
        >
          <Stack spacing={4}>
            <span className='typography-body1'>{t('shared.insituDataUpload.newConfig.labels.inSituDataUpload')}</span>
            <span className='typography-body1 text-text-disabled'>{t('global.widget.error.message')}</span>
          </Stack>
        </Card>
      )}
      onError={(error, info) => {
        Logger.error(`In-situ data upload rendering error: ${error} ${info}`);
      }}
    >
      <SuspenseBoundary
        fallback={
          <Stack centerMain>
            <Spinner size={10} />
          </Stack>
        }
      >
        <InSituDataCardsBase />
      </SuspenseBoundary>
    </ErrorBoundary>
  );
};

const InSituDataCardsBase = () => {
  const inSituConfigurations =
    useGetIndicatorProductSubscriptionConfigurations(undefined, true, {
      type: INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE.DEFAULT,
    }).data?.results ?? [];
  const { t } = useTranslation();
  const isStaffUser = useIsStaffUser();

  const filteredConfigurations = isStaffUser
    ? inSituConfigurations
    : inSituConfigurations.filter((c) => !isNullish(c.template_url));

  if (filteredConfigurations.length === 0) {
    return (
      <Stack spacing={4} className='gap-4 justify-start flex-wrap'>
        <span>{t('shared.insituDataUpload.newConfig.labels.noData')}</span>
        <AddNewConfig />
      </Stack>
    );
  }

  const sortedConfigurations = filteredConfigurations.sort((a, b) => a.indicator_name.localeCompare(b.indicator_name));

  return (
    <Stack spacing={4} className='gap-4 justify-start flex-wrap' direction='row'>
      {sortedConfigurations.map((config, i) => (
        <InSituDataCard key={`in-situ-config-${i}`} config={config} />
      ))}
      <AddNewConfig />
    </Stack>
  );
};

const InSituDataCard = ({ config }: { config: IndicatorProductSubscriptionConfiguration }) => {
  const { t } = useTranslation();
  const isStaffUser = useIsStaffUser();
  const { submit: updateSubmit, isSubmitting: isUpdating } = useUpdateIndicatorProductSubscriptionConfiguration();

  const updateDate = printDDMMYYYY(config.modified_at);

  const { indicator_name, status, id, template_url } = config;
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showFileUploadDialog, setShowFileUploadDialog] = useState(false);
  const canManageInsituData = useCanManageInsituData();

  const handleStatusUpdate = async (
    newStatus: (typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS)[keyof typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS],
  ) => {
    await updateSubmit(id, { status: newStatus });
  };

  const downLoadFile = (url: string | null) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const originalFileUrl = config.file_info && config.file_info.length > 0 ? config.file_info[0]?.url : null;

  return (
    <>
      <ActionCard
        data-testid='config-card'
        spacing={4}
        actionButtons={
          <Stack direction='row' spacing={2} className='justify-between w-full'>
            <Stack direction='row' spacing={isStaffUser ? 2 : 4} className='items-center'>
              {template_url && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      data-testid='config-card-download-template-button'
                      onClick={() => downLoadFile(template_url)}
                      className={BUTTON_STYLES}
                      variant='text'
                    >
                      <Icon icon='ri-file-excel-2-line' className='text-xl' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className='whitespace-pre-wrap'>
                    <Stack className='gap-4'>{t('shared.insituDataUpload.newConfig.button.template')}</Stack>
                  </TooltipContent>
                </Tooltip>
              )}
              {originalFileUrl && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      data-testid='config-card-download-original-file-button'
                      onClick={() => downLoadFile(originalFileUrl)}
                      className={BUTTON_STYLES}
                      variant='text'
                    >
                      <Icon icon='ri-download-cloud-line' className='text-xl' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className='whitespace-pre-wrap'>
                    <Stack className='gap-4'>{t('shared.insituDataUpload.newConfig.button.originalFile')}</Stack>
                  </TooltipContent>
                </Tooltip>
              )}
              {isStaffUser && (
                <StaffControls
                  id={id}
                  status={status}
                  isUpdating={isUpdating}
                  handleStatusUpdate={handleStatusUpdate}
                  setShowDeleteConfirmation={setShowDeleteConfirmation}
                />
              )}
            </Stack>
            {canManageInsituData && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid='config-card-upload-button'
                    onClick={() => setShowFileUploadDialog(true)}
                    className={BUTTON_STYLES}
                  >
                    <Icon icon='ri-upload-cloud-line' className='text-xl' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className='whitespace-pre-wrap'>
                  <Stack className='gap-4'>{t('shared.insituDataUpload.newConfig.button.upload')}</Stack>
                </TooltipContent>
              </Tooltip>
            )}
          </Stack>
        }
      >
        <Stack spacing={4} center centerMain className='grow'>
          <Stack center centerMain>
            <Icon icon='ri-folder-chart-line' className='text-7xl' />
          </Stack>

          <Stack spacing={2} center centerMain>
            <span className='typography-h4 whitespace-pre-wrap text-neutral-black-80'>{indicator_name}</span>
            <StatusPill status={status} />
            {updateDate && <span className='typography-caption text-neutral-black-60'>{updateDate}</span>}
          </Stack>
        </Stack>
      </ActionCard>
      <DeleteInSituConfigurationWithConfirmation
        config={showDeleteConfirmation ? config : null}
        onClose={() => setShowDeleteConfirmation(false)}
      />
      <InSituFileUploadDialog config={config} open={showFileUploadDialog} onOpenChange={setShowFileUploadDialog} />
    </>
  );
};

type StaffControlsProps = {
  id: string;
  status: (typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS)[keyof typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS];
  isUpdating: boolean;
  handleStatusUpdate: (
    newStatus: (typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS)[keyof typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS],
  ) => void;
  setShowDeleteConfirmation: (value: boolean) => void;
};

const StaffControls = ({
  id,
  status,
  isUpdating,
  handleStatusUpdate,
  setShowDeleteConfirmation,
}: StaffControlsProps) => {
  const { t } = useTranslation();

  return (
    <>
      <AddNewConfig asIcon={true} insituIpSubscriptionIdArg={id} />
      {(
        [
          INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.REJECTED,
          INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.VALIDATED,
        ] as const
      ).includes(status as 'rejected' | 'validated') && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              data-testid='config-card-show-button'
              onClick={() => handleStatusUpdate(INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.APPROVED)}
              disabled={isUpdating}
              className={BUTTON_STYLES}
            >
              <Icon icon='ri-eye-line' className='text-xl' />
            </Button>
          </TooltipTrigger>
          <TooltipContent className='whitespace-pre-wrap'>
            <Stack className='gap-4'>{t('shared.insituDataUpload.newConfig.button.show')}</Stack>
          </TooltipContent>
        </Tooltip>
      )}
      {status === INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.APPROVED && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              data-testid='config-card-hide-button'
              onClick={() => handleStatusUpdate(INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.REJECTED)}
              disabled={isUpdating}
              className={BUTTON_STYLES}
              variant='text'
            >
              <Icon icon='ri-eye-off-line' className='text-xl' />
            </Button>
          </TooltipTrigger>
          <TooltipContent className='whitespace-pre-wrap'>
            <Stack className='gap-4'>{t('shared.insituDataUpload.newConfig.button.hide')}</Stack>
          </TooltipContent>
        </Tooltip>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            data-testid='config-card-delete-button'
            onClick={() => setShowDeleteConfirmation(true)}
            className={`text-error hover:text-error-dark ${BUTTON_STYLES}`}
            variant='text'
          >
            <Icon icon='ri-delete-bin-line' className='text-xl' />
          </Button>
        </TooltipTrigger>
        <TooltipContent className='whitespace-pre-wrap'>
          <Stack className='gap-4'>{t('global.ui.buttons.delete')}</Stack>
        </TooltipContent>
      </Tooltip>
    </>
  );
};

export const StatusPill = ({
  status,
}: {
  status: (typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS)[keyof typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS];
}) => {
  const { t } = useTranslation();
  const style = getStyleForIPConfigStatus(status);

  return (
    <Pill
      className='typography-overline font-bold w-fit rounded-lg p-2'
      style={{
        color: style?.color,
        backgroundColor: style?.backgroundColor,
      }}
    >
      {t(`global.indicatorProductSubscriptionConfigurationStatus.${status}`)}
    </Pill>
  );
};

type DeleteInSituConfigurationWithConfirmationProps = {
  config: IndicatorProductSubscriptionConfiguration | null;
  onClose: () => void;
};

export const DeleteInSituConfigurationWithConfirmation = ({
  config,
  onClose,
}: DeleteInSituConfigurationWithConfirmationProps) => {
  const { t } = useTranslation();
  const { submit: deleteSubmit, isSubmitting: isDeleting } = useRemoveIndicatorProductSubscriptionConfiguration();

  const handleDelete = async () => {
    if (!config) return;

    await deleteSubmit(
      config.id,
      () => {
        toast({
          type: 'success',
          title: t('shared.insituDataUpload.newConfig.labels.deleteSuccess'),
        });
        onClose();
      },
      () => {
        toast({
          type: 'error',
          title: t('shared.insituDataUpload.newConfig.labels.deleteError'),
        });
      },
    );
  };

  return (
    <Dialog open={!!config} onOpenChange={onClose}>
      <DialogContent data-testid='delete-insitu-configuration-with-confirmation'>
        <DialogHeader className='typography-h2 w-full text-error md:typography-h3 sm:text-center'>
          {t('global.ui.buttons.delete')}
        </DialogHeader>
        <DialogBody className='mb-8'>
          <p>{t('shared.insituDataUpload.newConfig.labels.deleteConfirmation')}</p>
          {config?.indicator_name && (
            <p className='typography-body1Semibold text-center my-2'>{`${config?.indicator_name} (ID: ${config?.id})`}</p>
          )}
        </DialogBody>
        <DialogFooter className='justify-end'>
          <DialogClose asChild>
            <Button variant='text'>{t('global.ui.buttons.cancel')}</Button>
          </DialogClose>
          <Button onClick={handleDelete} loading={isDeleting} className='bg-error hover:bg-error-dark'>
            {t('global.ui.buttons.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
